// src/pages/Stats2026.js - Selection statistics: what everyone picked, per race or year to date
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';

const SEASON = 2026;
const YTD_PARAM = 'season';
const COLOUR_STORAGE_KEY = 'stats.barColour';

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

const TRACK = '#263247';
const YOU = '#fbbf24';          // amber, matches the app's points colour
const RESULT = '#0ca30c';       // the row that actually happened
const RESULT_DARK = '#0a5c0a';
const OTHER = '#4b5563';
const OTHER_DARK = '#2f3744';
// Driver head to head: green for the side with more picks, red for the other.
const SPLIT_MORE = '#16a34a';
const SPLIT_FEWER = '#dc2626';
const SPLIT_TIE = '#6b7280';

// Heat ramp on the dark surface: a dim ember for a rarely picked driver, bright
// and hot for a popular one. Every bar starts at the ember and runs up to the
// colour its share earns. 60% and above is max heat.
const EMBER = '#5a3a25';
const HEAT = ['#5a3a25', '#9a4a1c', '#cf5a1d', '#ee7a21', '#f8a43a', '#ffd166'];
const HEAT_CAP = 60;

const hex2rgb = (h) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
const rgb2hex = (rgb) => '#' + rgb.map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');
const mix = (a, b, t) => { const A = hex2rgb(a), B = hex2rgb(b); return rgb2hex(A.map((v, i) => v + (B[i] - v) * t)); };
const darken = (hex, amount = 0.5) => mix(hex, '#000000', amount);
const luminance = (hex) => { const [r, g, b] = hex2rgb(hex).map(v => v / 255); return 0.2126 * r + 0.7152 * g + 0.0722 * b; };
const inkOn = (hex) => (luminance(hex) > 0.45 ? '#111111' : '#ffffff');

const heat = (share) => {
  const x = Math.min(1, Math.max(0, share / HEAT_CAP)) * (HEAT.length - 1);
  const i = Math.min(HEAT.length - 2, Math.floor(x));
  return mix(HEAT[i], HEAT[i + 1], x - i);
};

// Team colours, matched on the team's id or name so a renamed or new team still lands.
const TEAM_COLOURS = [
  [/red.?bull/, '#3671C6'],
  [/racing.?bulls|\brb\b|alphatauri|toro.?rosso|visa/, '#6692FF'],
  [/mercedes/, '#27F4D2'],
  [/ferrari/, '#E8002D'],
  [/mclaren/, '#FF8000'],
  [/aston/, '#229971'],
  [/alpine/, '#0093CC'],
  [/williams/, '#64C4FF'],
  [/haas/, '#B6BABD'],
  [/audi|sauber|kick/, '#A6A6A6'],
  [/cadillac/, '#D4AF37'],
];
const teamColour = (team) => {
  const key = `${team?.id || ''} ${team?.name || ''}`.toLowerCase();
  const hit = TEAM_COLOURS.find(([re]) => re.test(key));
  return hit ? hit[1] : '#6b7280';
};

const readStoredColourMode = () => {
  try { const v = localStorage.getItem(COLOUR_STORAGE_KEY); return v === 'team' ? 'team' : 'heat'; } catch (_) { return 'heat'; }
};
const storeColourMode = (mode) => { try { localStorage.setItem(COLOUR_STORAGE_KEY, mode); } catch (_) {} };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORY_ORDER = ['P1', 'P2', 'P3', 'P4', 'P5', 'POLE', 'FL', 'DNF', 'MPG'];

const formatAustralianDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return `${formatAustralianDate(dateString)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Same closing rules the results page uses, so the race picker can flag closed rounds.
const isClosed = (race, session) => {
  if (!race) return false;
  const now = new Date();
  if (new Date(race.date) < now) return true;
  const quali = session === 'sprint' ? race.sprintQualifyingDateTime : race.qualifyingDateTime;
  if (!quali) return false;
  return new Date(new Date(quali).getTime() - 2 * 60 * 1000) < now;
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Chip({ kind, children }) {
  const cls = kind === 'you'
    ? 'bg-amber-400/20 text-amber-300 border-amber-400/60'
    : 'bg-green-500/20 text-green-300 border-green-500/60';
  return <span className={`text-[10px] leading-none font-semibold px-1.5 py-[3px] rounded border whitespace-nowrap ${cls}`}>{children}</span>;
}

/**
 * One heat strip: a full-width track, a gradient fill from the ember up to the
 * colour the share earns, and the percentage riding inside the fill when it fits.
 */
// Width of an element in pixels, kept current as the layout changes.
function useMeasuredWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => setWidth(node.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(node);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return [ref, width];
}

const LABEL_MIN_PX = 46; // room for "100%" plus padding

function ShareRow({ code, name, pct, count, total, colourMode, team, variant, isMine, isResult }) {
  const [stripRef, stripWidth] = useMeasuredWidth();
  let from, to, glow = 'none';
  if (variant === 'result') { from = RESULT_DARK; to = RESULT; }
  else if (variant === 'other') { from = OTHER_DARK; to = OTHER; }
  else if (colourMode === 'team') { const c = teamColour(team); from = darken(c); to = c; }
  else { from = EMBER; to = heat(pct); if (pct >= 30) glow = `0 0 12px ${to}66`; }

  // The percentage rides inside the fill only when the fill is actually wide
  // enough for it on screen; otherwise it sits just past the fill's end.
  const inside = (stripWidth * pct) / 100 >= LABEL_MIN_PX;
  const muted = variant === 'other';

  return (
    <div
      className="grid items-center gap-2"
      style={{ gridTemplateColumns: 'minmax(92px, 120px) 1fr 58px' }}
      title={`${count} of ${total}${isResult ? ' · actual result' : ''}`}
    >
      <div className={`flex items-baseline gap-1.5 min-w-0 text-[13px] ${muted ? 'text-gray-400' : 'text-gray-100'}`}>
        <span className="font-mono font-bold tracking-wide">{code}</span>
        <span className="text-xs text-gray-400 truncate hidden sm:inline">{name}</span>
      </div>
      <div
        ref={stripRef}
        className="relative h-[22px] rounded-[5px] overflow-hidden"
        style={{ background: TRACK, outline: isMine ? `2px solid ${YOU}` : 'none', outlineOffset: -2 }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-[5px]"
          style={{ width: `${pct}%`, minWidth: 6, background: `linear-gradient(90deg, ${from}, ${to})`, boxShadow: glow }}
        />
        <div
          className="absolute inset-y-0 flex items-center text-xs font-semibold tabular-nums"
          style={inside
            ? { left: 0, width: `${pct}%`, justifyContent: 'flex-end', paddingRight: 7, color: inkOn(to) }
            : { left: `${pct}%`, paddingLeft: 7, color: '#c4c9d2' }}
        >
          {pct}%
        </div>
      </div>
      <div className="flex justify-end items-center gap-1">
        {isMine && <Chip kind="you">you</Chip>}
        {isResult && <Chip kind="result">✓</Chip>}
      </div>
    </div>
  );
}

function CategoryCard({ category, myPick, actual, colourMode }) {
  const { label, total, rows, other } = category;
  return (
    <div className="bg-gray-800 rounded-lg px-4 pt-3 pb-3 min-w-0">
      <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300 mb-0.5">{label}</h3>
      <div className="text-xs text-gray-400 mb-2.5">{total} pick{total === 1 ? '' : 's'}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-gray-500">No picks</div>
      ) : (
        <div className="grid gap-1.5">
          {rows.map(row => {
            const isResult = !!actual && actual === row.id;
            return (
              <ShareRow
                key={row.id}
                code={row.code}
                name={row.name}
                pct={row.pct}
                count={row.count}
                total={total}
                colourMode={colourMode}
                team={row.team}
                variant={isResult ? 'result' : 'pick'}
                isMine={!!myPick && myPick === row.id}
                isResult={isResult}
              />
            );
          })}
          {other && (
            <ShareRow
              code="Other"
              name={`${other.drivers} driver${other.drivers === 1 ? '' : 's'}`}
              pct={other.pct}
              count={other.count}
              total={total}
              colourMode={colourMode}
              variant="other"
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Colours for the two sides of a split. Driver splits go green (more picks) and
 * red (fewer picks); team splits take each team's colour.
 */
const splitColours = (data, kind) => {
  if (kind === 'team') return [teamColour(data.option1), teamColour(data.option2)];
  if (data.option1.count === data.option2.count) return [SPLIT_TIE, SPLIT_TIE];
  return data.option1.count > data.option2.count ? [SPLIT_MORE, SPLIT_FEWER] : [SPLIT_FEWER, SPLIT_MORE];
};

function SplitCard({ title, data, myPick, kind }) {
  if (!data) {
    return (
      <div className="bg-gray-800 rounded-lg px-4 py-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300 mb-1">{title}</h3>
        <div className="text-sm text-gray-500">No head to head set for this session.</div>
      </div>
    );
  }
  const { option1, option2, total, winner } = data;
  const [c1, c2] = splitColours(data, kind);
  const p1 = total ? option1.pct : 50;
  const p2 = total ? 100 - p1 : 50;
  const winnerLabel = winner === option1.id || winner === option1.name ? option1.code
    : winner === option2.id || winner === option2.name ? option2.code
    : null;

  const Side = ({ opt, color, right }) => (
    <div className={`min-w-0 ${right ? 'text-right' : ''}`}>
      <div className={`font-mono font-bold text-[15px] inline-flex items-center gap-1.5 ${right ? 'flex-row-reverse' : ''}`}>
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <span className="truncate">{opt.code}</span>
        {myPick && (myPick === opt.id || myPick === opt.name) && <Chip kind="you">you</Chip>}
      </div>
      <div className="text-[22px] font-semibold tabular-nums">{total ? `${right ? p2 : p1}%` : '—'}</div>
    </div>
  );

  return (
    <div className="bg-gray-800 rounded-lg px-4 pt-3 pb-3 min-w-0">
      <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300 mb-0.5">{title}</h3>
      <div className="text-xs text-gray-400 mb-2.5">{total} pick{total === 1 ? '' : 's'}</div>
      <div className="grid items-center gap-2.5 mb-2" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
        <Side opt={option1} color={c1} />
        <div className="text-[11px] uppercase tracking-widest text-gray-400">vs</div>
        <Side opt={option2} color={c2} right />
      </div>
      <div className="flex h-[22px] gap-0.5 rounded overflow-hidden" title={`${option1.code} ${option1.count} · ${option2.code} ${option2.count}`}>
        <span className="block h-full rounded-l" style={{ width: `${p1}%`, background: total ? `linear-gradient(90deg, ${darken(c1)}, ${c1})` : TRACK }} />
        <span className="block h-full rounded-r" style={{ width: `${p2}%`, background: total ? `linear-gradient(90deg, ${c2}, ${darken(c2)})` : TRACK }} />
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-1.5 gap-2">
        <span>{option1.count} picked {option1.code}</span>
        <span className={winnerLabel ? 'text-green-300' : ''}>{winnerLabel ? `✓ ${winnerLabel} won` : 'Result pending'}</span>
        <span>{option2.count} picked {option2.code}</span>
      </div>
    </div>
  );
}

function Legend({ colourMode, showKeys }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-400 -mt-3 mb-7">
      {colourMode === 'heat' ? (
        <>
          <span>Fewer picks</span>
          <span className="inline-block h-2 w-40 rounded" style={{ background: `linear-gradient(90deg, ${HEAT.join(', ')})` }} />
          <span>More picks</span>
        </>
      ) : (
        <span>Bars take the driver's team colour</span>
      )}
      {showKeys && (
        <>
          <span className="inline-flex items-center gap-1 ml-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: RESULT }} /> Actual result
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ outline: `2px solid ${YOU}`, outlineOffset: -2 }} /> Your pick
          </span>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function Stats2026() {
  const { raceId } = useParams();
  const navigate = useNavigate();

  const [races, setRaces] = useState([]);
  const [racesLoading, setRacesLoading] = useState(true);
  const [error, setError] = useState(null);

  const isYtd = raceId === YTD_PARAM;
  const [session, setSession] = useState('feature');
  const [colourMode, setColourMode] = useState(readStoredColourMode);
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  const chooseColourMode = (mode) => { setColourMode(mode); storeColourMode(mode); };

  // Load the season's races once.
  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setRacesLoading(true);
        const response = await apiClient.get('/api/f1data/races/all');
        if (!Array.isArray(response.data)) { setError('Invalid race data'); return; }
        const seasonRaces = response.data
          .filter(r => r?.date && new Date(r.date).getFullYear() === SEASON)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setRaces(seasonRaces);
      } catch (err) {
        setError('Failed to load races.');
      } finally {
        setRacesLoading(false);
      }
    };
    fetchRaces();
  }, []);

  // With no race in the URL, land on the most recently closed round.
  useEffect(() => {
    if (racesLoading || raceId || races.length === 0) return;
    const closed = races.filter(r => isClosed(r, 'feature'));
    const target = closed[closed.length - 1] || races[0];
    if (target) navigate(`/2026/stats/${target._id}`, { replace: true });
  }, [racesLoading, raceId, races, navigate]);

  const selectedRace = !isYtd ? races.find(r => r._id === raceId) : null;

  // A non-sprint race has no sprint session: the Sprint tab choice is kept for
  // the next sprint weekend, but feature stats are what gets requested and shown.
  const activeSession = (!isYtd && selectedRace && !selectedRace.isSprint) ? 'feature' : session;

  const fetchStats = useCallback(async () => {
    if (!isYtd && !selectedRace) return;
    setDataLoading(true);
    setData(null);
    try {
      const url = isYtd
        ? `/api/stats/season/${SEASON}?session=${activeSession}`
        : `/api/stats/race/${selectedRace._id}?session=${activeSession}`;
      const response = await apiClient.get(url);
      setData(response.data);
    } catch (err) {
      setData({ error: err.response?.data?.message || 'Failed to load statistics.' });
    } finally {
      setDataLoading(false);
    }
  }, [isYtd, selectedRace, activeSession]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleRaceChange = (e) => {
    const id = e.target.value;
    if (id) navigate(`/2026/stats/${id}`);
  };

  const goToLatestRace = () => {
    const closed = races.filter(r => isClosed(r, 'feature'));
    const target = closed[closed.length - 1] || races[0];
    if (target) navigate(`/2026/stats/${target._id}`);
  };

  if (racesLoading) return <div className="container mx-auto px-4 py-8"><p className="text-center py-10">Loading races...</p></div>;
  if (error) return <div className="container mx-auto px-4 py-8"><div className="bg-red-900 text-red-200 px-4 py-3 rounded">{error}</div></div>;

  const showSessionTabs = isYtd || !!selectedRace?.isSprint;
  const categories = data?.categories
    ? CATEGORY_ORDER.filter(k => data.categories[k]).map(k => ({ key: k, ...data.categories[k] }))
    : [];

  const segBtn = (active) => `px-3.5 py-2 text-sm ${active ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Selection Statistics</h1>
      <p className="text-gray-400 mb-6">What everyone picked, as a share of all tippers. Available once predictions close for a session.</p>

      {/* Scope + race picker */}
      <div className="flex flex-wrap gap-2.5 items-center mb-3">
        <div className="inline-flex rounded-lg border border-gray-600 overflow-hidden" role="group" aria-label="Scope">
          <button type="button" onClick={() => { if (isYtd) goToLatestRace(); }} aria-pressed={!isYtd} className={segBtn(!isYtd)}>
            Single race
          </button>
          <button type="button" onClick={() => navigate(`/2026/stats/${YTD_PARAM}`)} aria-pressed={isYtd} className={`border-l border-gray-600 ${segBtn(isYtd)}`}>
            Year to date
          </button>
        </div>
        <select
          value={selectedRace?._id || ''}
          onChange={handleRaceChange}
          disabled={isYtd}
          aria-label="Race"
          className="flex-1 min-w-[240px] px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-gray-100 disabled:opacity-50"
        >
          <option value="">-- Select a race --</option>
          {races.map(race => (
            <option key={race._id} value={race._id}>
              {race.round ? `R${race.round} · ` : ''}{race.raceName} — {formatAustralianDate(race.date)}{isClosed(race, 'feature') ? '' : ' (open)'}
            </option>
          ))}
        </select>
      </div>

      {/* Session tabs + bar colour */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex gap-1.5">
          {showSessionTabs && ['feature', 'sprint'].map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setSession(s)}
              aria-pressed={activeSession === s}
              className={`px-3 py-1 text-[13px] rounded-full border ${activeSession === s ? 'bg-gray-700 border-gray-700 text-gray-100' : 'border-gray-600 text-gray-300 hover:text-white'}`}
            >
              {s === 'feature' ? '🏁 Feature' : '⚡ Sprint'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>Bar colour</span>
          <div className="inline-flex rounded-md border border-gray-600 overflow-hidden" role="group" aria-label="Bar colour">
            <button type="button" onClick={() => chooseColourMode('heat')} aria-pressed={colourMode === 'heat'}
              className={`px-2.5 py-1 ${colourMode === 'heat' ? 'bg-gray-700 text-gray-100' : 'bg-gray-800 text-gray-300 hover:text-white'}`}>
              Heat by share
            </button>
            <button type="button" onClick={() => chooseColourMode('team')} aria-pressed={colourMode === 'team'}
              className={`px-2.5 py-1 border-l border-gray-600 ${colourMode === 'team' ? 'bg-gray-700 text-gray-100' : 'bg-gray-800 text-gray-300 hover:text-white'}`}>
              Team colours
            </button>
          </div>
        </div>
      </div>

      {/* Context line */}
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-4">
        {isYtd ? (
          <>
            <h2 className="text-xl font-bold">{SEASON} season to date</h2>
            {data?.rounds && (
              <span className="text-[13px] text-gray-400">
                {data.rounds.length} {activeSession === 'sprint' ? 'sprint' : 'feature'} session{data.rounds.length === 1 ? '' : 's'} closed · {data.entries} race entries pooled · {data.tippers} tippers
              </span>
            )}
          </>
        ) : selectedRace ? (
          <>
            <h2 className="text-xl font-bold">{selectedRace.raceName}</h2>
            <span className="text-[13px] text-gray-400">
              {selectedRace.round ? `Round ${selectedRace.round} · ` : ''}{formatAustralianDate(selectedRace.date)}
            </span>
            {selectedRace.isSprint && <span className="text-xs bg-purple-800 text-purple-200 px-2 py-0.5 rounded-full">Sprint Weekend</span>}
            {data?.closed && (
              <span className="text-[13px] text-gray-400">
                {data.tippers} tipper{data.tippers === 1 ? '' : 's'} · {data.actual ? 'results published' : 'closed, results pending'}
              </span>
            )}
          </>
        ) : null}
        <Link to={!isYtd && selectedRace ? `/2026/results/${selectedRace._id}` : '/2026/results'} className="text-[13px] text-blue-400 hover:text-blue-300 ml-auto">
          ← Back to results
        </Link>
      </div>

      {dataLoading ? (
        <div className="p-8 text-center text-gray-400">Loading...</div>
      ) : data?.error ? (
        <div className="bg-red-900 text-red-200 px-4 py-3 rounded">{data.error}</div>
      ) : !isYtd && data && data.closed === false ? (
        <div className="p-4 bg-blue-900 border border-blue-700 text-blue-200 rounded">
          <p className="font-semibold">{activeSession === 'sprint' ? '⚡ Sprint predictions still open' : '🏁 Feature predictions still open'}</p>
          <p className="text-sm mt-1">
            Selection statistics appear once {activeSession === 'sprint' ? 'sprint ' : ''}qualifying begins
            {data.closesAt ? ` (${formatDateTime(data.closesAt)})` : ''}, so nobody can see the field before locking in their own picks.
          </p>
        </div>
      ) : isYtd && data && !data.categories ? (
        <div className="p-4 bg-gray-700 text-gray-300 rounded">No {activeSession} sessions have closed yet this season.</div>
      ) : data ? (
        <>
          <div className="grid gap-3.5 mb-7" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {categories.map(cat => (
              <CategoryCard
                key={cat.key}
                category={cat}
                myPick={data.myPicks?.[cat.key]}
                actual={data.actual?.[cat.key]}
                colourMode={colourMode}
              />
            ))}
          </div>

          <Legend colourMode={colourMode} showKeys={!isYtd} />

          {!isYtd && (
            <>
              <h2 className="text-base font-bold text-gray-300 mb-2.5">Head to head</h2>
              <div className="grid gap-3.5 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                <SplitCard title="Driver head to head" kind="driver" data={data.headToHead?.drivers} myPick={data.myPicks?.driverHeadToHead} />
                <SplitCard title="Team head to head" kind="team" data={data.headToHead?.teams} myPick={data.myPicks?.teamHeadToHead} />
              </div>
            </>
          )}

          <p className="text-xs text-gray-500 mt-4">
            Share = picks for that driver ÷ tippers who made a pick in that category.
            {isYtd && ' Year to date pools every closed round, so one tipper counts once per round.'}
          </p>
        </>
      ) : null}
    </div>
  );
}

export default Stats2026;
